import React, { useState, useEffect, useMemo } from 'react';
import { formatStorageUrl } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { Song } from '@/types/player';

interface PlayerCoverImageProps {
  song: Song;
}

export default function PlayerCoverImage({ song }: PlayerCoverImageProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(true);

  // Memoiza a URL da imagem para evitar recálculos desnecessários
  const imageUrl = useMemo(() => formatStorageUrl(song.capa_url || song.cover_url), [song.capa_url, song.cover_url]);

  // Pré-carrega a imagem quando a URL mudar
  useEffect(() => {
    if (!imageUrl) return;

    // Reseta estados
    setImageLoaded(false);
    setImageError(false);
    setShowSkeleton(true);

    // Pré-carrega a imagem
    const img = new Image();
    img.src = imageUrl;
    
    // Se a imagem já estiver em cache, isso dispara imediatamente
    img.onload = () => {
      setImageLoaded(true);
      // Pequeno delay para suavidade visual
      setTimeout(() => setShowSkeleton(false), 50);
    };
    
    img.onerror = () => {
      setImageError(true);
      setImageLoaded(true);
      setShowSkeleton(false);
    };

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl]);

  // Skeleton animado - Memoizado para evitar recriação
  const SkeletonLoader = useMemo(() => (
    <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse flex items-center justify-center">
      <div className="w-20 h-20 bg-gray-400 rounded-full opacity-30 animate-bounce">
        <div className="w-full h-full flex items-center justify-center">
          <span className="text-gray-500 text-2xl">♪</span>
        </div>
      </div>
    </div>
  ), []);

  // Fallback para erro ou sem imagem - Memoizado para evitar recriação
  const FallbackCover = useMemo(() => (
    <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
      <svg className="w-24 h-24 text-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
      </svg>
    </div>
  ), []);

  if (!imageUrl || imageError) {
    return FallbackCover;
  }

  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Skeleton loader */}
      <div className={cn(
        "absolute inset-0 transition-opacity duration-300",
        showSkeleton ? "opacity-100" : "opacity-0"
      )}>
        {SkeletonLoader}
      </div>
      
      {/* Imagem real */}
      <img 
        src={imageUrl}
        alt={song.descricao}
        className={cn(
          "w-full h-full object-cover transition-opacity duration-300",
          imageLoaded && !imageError ? "opacity-100" : "opacity-0"
        )}
        loading="eager"
        decoding="async"
        onLoad={() => {
          setImageLoaded(true);
          setTimeout(() => setShowSkeleton(false), 50);
        }}
        onError={() => {
          setImageError(true);
          setImageLoaded(true);
          setShowSkeleton(false);
        }}
      />
    </div>
  );
} 